import React, { useEffect, useState } from "react";
import axios from "axios";
import { formatSalesValue } from "../../utils/formatUtils";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { formatDisplayDate } from "../../utils/formateDateUtils";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

const WeeklySalesByCompany = ({ banquetUrls }) => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const todayStr = new Date().toLocaleDateString('en-CA');

    const fetchData = async () => {
        try {
            if (!banquetUrls?.length) return;

            setLoading(true);
            setError(null);

            const responses = await Promise.all(
                banquetUrls.map((outlet) =>
                    axios.post(
                        `${outlet.url}/lastSevenDaysOutletSalesDashboard`,
                        { token: outlet.token }
                    )
                )
            );

            const outlets = responses
                .map((response, index) => ({
                    id: banquetUrls[index].id,
                    ...response.data.data[0],
                }))
                .filter(Boolean);

            if (!outlets.length) {
                throw new Error("No data found");
            }

            const sortedDates = [...outlets[0].dates].reverse();

            const labels = sortedDates.map((d) =>
                d.date === todayStr
                    ? "Today"
                    : formatDisplayDate(d.date)
            );

            const buildDataset = (source, type) => {
                return source.dates
                    .slice()
                    .reverse()
                    .map((d) => {
                        if (d.type === type) return d.sales;

                        if (
                            type === "future" &&
                            d.date === todayStr &&
                            d.type === "actual"
                        ) {
                            return d.sales;
                        }

                        return null;
                    });
            };

            const colors = [
                "#118AB2",
                "#FF6B6B",
                "#06D6A0",
                "#FFD166",
                "#8E44AD",
                "#F97316",
                "#14B8A6",
            ];

            const datasets = outlets.flatMap((outlet, index) => {
                const color = colors[index % colors.length];

                const label =
                    outlet.name ||
                    outlet.Outlet_Name ||
                    `Outlet ${outlet.id}`;

                return [
                    {
                        label: `${label} - Actual`,
                        data: buildDataset(outlet, "actual"),
                        borderColor: color,
                        backgroundColor: color,
                        tension: 0.3,
                        spanGaps: true,
                    },
                    {
                        label: `${label} - Projected`,
                        data: buildDataset(outlet, "future"),
                        borderColor: color,
                        backgroundColor: color,
                        borderDash: [6, 6],
                        tension: 0.3,
                        spanGaps: true,
                    },
                ];
            });

            setChartData({
                labels,
                datasets,
            });
        } catch (error) {
            console.error("Error fetching weekly sales:", error);

            setError(
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                "Failed to load weekly sales data."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (banquetUrls?.length) {
            fetchData();
        }
    }, [banquetUrls]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: "index",
            intersect: false,
        },
        plugins: {
            legend: {
                position: "bottom",
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const isProjected =
                            context.dataset.label.includes("Projected");

                        const isToday = context.label === "Today";

                        if (isProjected && isToday) {
                            return null;
                        }

                        if (context.raw == null) return null;

                        return `${context.dataset.label}: ${formatSalesValue(
                            context.raw
                        )}`;
                    },
                },
            },
        },
        scales: {
            x: {
                ticks: {
                    autoSkip: false
                }
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Sales",
                },
            },
        },
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-3 lg:p-4 w-full">
            <h1 className="text-lg lg:text-xl text-gray-700 font-semibold text-center mb-4">
                Actual vs Projected Sales
            </h1>

            {loading && (
                <div className="text-center font-semibold">
                    Loading...
                </div>
            )}

            {error && (
                <div className="text-center text-red-600 font-medium">
                    {error}
                </div>
            )}

            {!loading && !error && chartData && (
                <div className="overflow-x-auto">
                    <div className="min-w-[650px] h-[400px]">
                        <Line data={chartData} options={options} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default WeeklySalesByCompany;
